import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import EditIcon from '~/assets/icons/penEdit.svg';
import TrashIcon from '~/assets/icons/trash.svg';

const PopupContainer = styled.div`
  position: absolute;
  background: white;
  border: 0px solid #e1e5e9;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 120px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  &:hover {
    background: #f5f5f5;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditIconForButton = styled(EditIcon)`
  path {
    fill: #666;
  }
`;
const TrashIconForButton = styled(TrashIcon)`
  path {
    fill: #666;
  }
`;

interface FolderPopupMenuProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  folderId: string;
  onEdit: () => void;
}

const FolderPopupMenu: React.FC<FolderPopupMenuProps> = ({
  isOpen,
  anchorElement,
  onClose,
  folderId,
  onEdit
}) => {
  const { removeSubItem } = useEditorStore();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.right + window.scrollX - 120
      });
    }
  }, [isOpen, anchorElement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleEdit = () => {
    onEdit();
    onClose();
  };

  const handleDelete = () => {
    const realFolderId = folderId.replace('_feedback', '');
    const numericFolderId = parseInt(realFolderId, 10);

    removeSubItem(numericFolderId);
    onClose();
  };

  if (!isOpen || !anchorElement) {
    return null;
  }

  return ReactDOM.createPortal(
    <PopupContainer
      ref={popupRef}
      style={{ top: position.top, left: position.left }}
    >
      <Button onClick={handleEdit}>
        <EditIconForButton /> Edit
      </Button>
      <Button onClick={handleDelete}>
        <TrashIconForButton style={{ marginLeft: '-5px', marginTop: '-5px' }} />{' '}
        Delete
      </Button>
    </PopupContainer>,
    document.body
  );
};

export default FolderPopupMenu;
